import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export const defaultPaletteName = '特殊カード専用パレット';

export type PaletteDb = {
  name: string;
  standard: boolean;
  goodsId?: string;
  version?: number;
}
const initial: PaletteDb = {
  name: '',
  standard: false,
  goodsId: undefined,
  version: undefined
};

export type PaletteDbHashType = {
  [key in string]: PaletteDb;
}

export type PaletteAddPayload = {
  id?: string;
} & PaletteDb;
export type PaletteUpdatePayload = {
  id: string;
} & { [P in keyof PaletteDb]?: PaletteDb[P] };

export type PaletteDbState = {
  palettes: PaletteDbHashType;
}

export const generatePaletteId = (): string => generateId('p');
let initialState: PaletteDbState = { palettes: {} };
const key = 'palettes';

const paletteDbSlice = createSlice({
  name: 'paletteDb',
  initialState,
  reducers: {
    initializePalettes(state: PaletteDbState) {
      state.palettes = {};
    },
    addPalette(state: PaletteDbState, action: PayloadAction<PaletteAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generatePaletteId();
      state.palettes[id] = obj;
      saveData(key, id, obj);
    },
    updatePalette(state: PaletteDbState, action: PayloadAction<PaletteUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.palettes[id] === undefined) {
        console.log(`*** Palette not found: ${id}`);
      } else {
        const newData = { ...state.palettes[id], ...obj };
        state.palettes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deletePalette(state: PaletteDbState, action: PayloadAction<PaletteUpdatePayload>) {
      const id = action.payload.id;
      delete state.palettes[id];
      deleteData(key, id);
    },
    savePalettes(state: PaletteDbState) {
      saveAllData(key, state.palettes);
    },
    signUpPalettes(state: PaletteDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.palettes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: PaletteDbState, action) => {
      const values = action.payload;
      const [changed, preparingValues] = updateHashProps(initial, values);
      const updatedValues: PaletteDbHashType = {};
      Object.keys(preparingValues).forEach(id => {
        const value = preparingValues[id];
        if (value.standard && value.name !== defaultPaletteName) updatedValues[id] = { ...value, name: defaultPaletteName };
        else updatedValues[id] = value;
      })
      state.palettes = updatedValues;
      if (changed) saveAllData(key, state.palettes);
    })
  }
})

export const { initializePalettes, addPalette, updatePalette, deletePalette, savePalettes, signUpPalettes } = paletteDbSlice.actions;
export default paletteDbSlice.reducer;
